/**
 * Which data a Card Management tab is rendering (EX-40600).
 *
 * Shared by the Activity and Statements seams so the two tabs on the same page
 * cannot drift apart on the one judgement that matters most here: whether the
 * user is looking at a real read, a fabricated stand-in, or a failure.
 *
 * The permissive direction is the dangerous one. Presenting demo rows as live
 * puts invented balances and invented audit history on screen with nothing
 * marking them — so anything not positively known to be real resolves to
 * `demo` (badged) or `error` (surfaced), never silently to `live`.
 */

export type CardManagementSource = 'loading' | 'live' | 'demo' | 'error'

/**
 * HTTP statuses that mean "this card has nothing to show", as distinct from
 * "the read failed".
 *
 * `403 CARD-MGMT.3` is the card or organization not being demo-enrolled;
 * `404` is either an unroutable path (these routes are non-production mocks
 * registered only for certain Spring profiles) or a card the actor cannot see.
 * None is a fault the user can act on or that retrying fixes — each is the
 * server saying there is nothing here — so they resolve to the demo surface
 * rather than to an error panel.
 *
 * Everything else (`4xx` we did not name, `5xx`, network failures, a malformed
 * response) stays an error. A `503 CARD-MGMT.15` is a transient outage, and
 * quietly substituting fabricated figures for it would misreport the card as
 * known-and-empty.
 */
const UNPROVISIONED_STATUSES = new Set([403, 404])

export const isUnprovisioned = (error: unknown): boolean =>
  error !== null &&
  typeof error === 'object' &&
  'status' in error &&
  typeof error.status === 'number' &&
  UNPROVISIONED_STATUSES.has(error.status)

/**
 * @param isLoading - True while the first read is in flight. Resolves to
 *   `loading` ahead of every other branch, so a tab never paints demo rows it
 *   is about to replace — the flash that made fabricated figures look real for
 *   a frame.
 * @param isError - Whether the read failed.
 * @param error - The failure, classified by `isUnprovisioned`.
 * @param hasData - Whether the successful read actually returned anything. A
 *   `200` with nothing in it is the contract's documented answer for a card
 *   with no history, and is the case the demo surface exists for.
 */
export const resolveCardManagementSource = ({
  isLoading,
  isError,
  error,
  hasData,
}: {
  isLoading: boolean
  isError: boolean
  error: unknown
  hasData: boolean
}): CardManagementSource => {
  if (isLoading) {
    return 'loading'
  }
  if (isError) {
    return isUnprovisioned(error) ? 'demo' : 'error'
  }
  return hasData ? 'live' : 'demo'
}
